/**
 * Inline help-tip — the small ⓘ icon beside a label that reveals an
 * explanatory popover on hover/focus. Ports the design's `.tip` affordance
 * (a styled dark popover with arrow + max-width) to a shadcn/Radix Tooltip so
 * it matches the rest of the app instead of the browser's native `title`
 * bubble.
 *
 * `TooltipProvider` is mounted app-wide in App.tsx, so this can be dropped in
 * anywhere without its own provider.
 */
import { Info } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface InfoTipProps {
  /** Explanatory text shown in the popover. */
  text: string;
  /** Override the icon size (defaults to the inline `h-3 w-3` help icon). */
  iconClassName?: string;
}

export function InfoTip({ text, iconClassName }: InfoTipProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          // Stop clicks from bubbling to a parent label/CollapsibleTrigger so
          // tapping the icon never focuses a field or toggles a panel.
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          className="inline-flex cursor-help text-muted-foreground transition-colors hover:text-foreground"
          aria-label="More information"
        >
          <Info className={cn("h-3 w-3", iconClassName)} aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[240px] text-xs font-medium leading-relaxed">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}
